import {
  ApolloClient,
  ApolloLink,
  DefaultOptions,
  InMemoryCache,
  Observable,
} from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { createUploadLink } from "apollo-upload-client"
// import { toast } from "react-toastify";

const GRAPHQL_URI =
  process.env.REACT_APP_GRAPHQL_URI || "http://localhost:3001/graphql"

const authMiddleware = new ApolloLink((operation: any, forward: any) => {
  const token = localStorage.getItem("token") || null
  operation.setContext({
    headers: {
      authorization: `Bearer ${token}`,
    },
  })
  return forward(operation)
})

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  },
  query: {
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  },
  mutate: {
    errorPolicy: "all",
  },
}

const signOutUser = (_forward?: Function, _operation?: any) => {
  localStorage.removeItem("token")
  localStorage.removeItem("refreshToken")
  localStorage.removeItem("previousToken")
  localStorage.removeItem("previousRefreshToken")
  localStorage.removeItem("previousSideBarItem")
  // Navigate to login. Do NOT re-forward the operation — the token is gone, so
  // re-sending it would just hit the server with an empty Bearer and log a
  // spurious "jwt malformed" error.
  window.location.href = "/"
}

/**
 * Ends an expired impersonation session cleanly: restores the admin's own
 * stashed tokens and reloads, instead of a hard sign-out. Impersonation
 * sessions carry no refreshToken (loginAsImpersonation issues none), so this
 * is the only way an expired ~20m impersonation access token can recover —
 * mirrors loginAsWithPrevious but skips the refresh-token revoke (there is
 * none to revoke).
 */
const restorePreviousSessionAndReload = (): void => {
  const preToken = localStorage.getItem("previousToken")
  const previousSideBarItem = localStorage.getItem("previousSideBarItem")
  const previousRefreshToken = localStorage.getItem("previousRefreshToken")

  localStorage.removeItem("token")
  localStorage.removeItem("sideBarItem")
  localStorage.removeItem("refreshToken")
  localStorage.setItem("token", preToken || "")
  if (previousSideBarItem) {
    localStorage.setItem("sideBarItem", previousSideBarItem)
  }
  previousRefreshToken
    ? localStorage.setItem("refreshToken", previousRefreshToken)
    : localStorage.removeItem("refreshToken")
  localStorage.removeItem("previousToken")
  localStorage.removeItem("previousRefreshToken")
  localStorage.removeItem("previousSideBarItem")
  window.location.reload()
}

// ---------------------------------------------------------------------------
// Token refresh: outcome types, config, and cross-tab coordination
// ---------------------------------------------------------------------------

// Web Locks lock name shared by every tab so a wake-from-sleep storm across
// tabs serializes into a single refresh attempt instead of racing.
const REFRESH_LOCK_NAME = "realrepp-ats-token-refresh"
const REFRESH_MAX_ATTEMPTS = 3
// Backoff before attempts 2 and 3 — covers the "wake from sleep before Wi-Fi
// reattaches" window without stalling the UI for long.
const REFRESH_RETRY_DELAYS_MS = [300, 900]

// GraphQL error codes that mean the server has definitively rejected the
// refresh token (as opposed to a transient network/server hiccup). Only these
// warrant a sign-out. Kept in lockstep with the codes thrown by
// RefreshTokenService.rotate() / auth.resolver.ts on the API side.
const DEFINITIVE_REFRESH_ERROR_CODES = new Set([
  "REFRESH_TOKEN_INVALID",
  "REFRESH_TOKEN_EXPIRED",
  "REFRESH_TOKEN_REUSE",
  "USER_NOT_FOUND",
])

export type RefreshResult =
  | { status: "ok"; token: string }
  | { status: "definitive" }
  | { status: "transient" }

export type RefreshOutcome = "retry" | "signout" | "preserve"

export const refreshResultToOutcome = (r: RefreshResult): RefreshOutcome =>
  r.status === "ok" ? "retry" : r.status === "definitive" ? "signout" : "preserve"

const supportsWebLocks = (): boolean =>
  typeof navigator !== "undefined" &&
  !!(navigator as any).locks &&
  typeof (navigator as any).locks.request === "function"

// ---------------------------------------------------------------------------
// Single-flight refresh mutex (fallback path when Web Locks is unavailable)
// ---------------------------------------------------------------------------
let isRefreshing = false
let pendingResolvers: Array<(result: RefreshResult) => void> = []

// Latch for exiting an expired impersonation session. A page fires many queries
// at once, so multiple will hit the expiry in the same tick. The first restores
// the admin's session (which clears previousToken, then reloads); this latch
// makes the siblings no-op instead of racing into signOutUser once previousToken
// is gone — the race that otherwise kicks the admin to the login page.
let isRestoringImpersonation = false

const notifyPendingResolvers = (result: RefreshResult): void => {
  pendingResolvers.forEach((resolve) => resolve(result))
  pendingResolvers = []
}

// Test-only: reset the module-level refresh mutex/queue between test cases.
export const __resetRefreshStateForTests = (): void => {
  isRefreshing = false
  pendingResolvers = []
}

/**
 * Calls the refreshToken mutation via a bare fetch so it bypasses this very
 * error link (avoids recursive interception). Retries transient failures
 * (network throws, non-JSON bodies, HTTP errors without a definitive GraphQL
 * error code) up to `maxAttempts` with a short backoff — this is what covers
 * wake-from-sleep before Wi-Fi reattaches. A definitive server verdict
 * (REFRESH_TOKEN_INVALID/EXPIRED/REUSE, USER_NOT_FOUND) short-circuits
 * immediately with no retry. Only a successful response writes to
 * localStorage; transient failures never touch it.
 */
export const performTokenRefresh = async (
  refreshToken: string,
  deps: {
    fetchImpl?: typeof fetch
    sleep?: (ms: number) => Promise<void>
    maxAttempts?: number
  } = {}
): Promise<RefreshResult> => {
  const doFetch = deps.fetchImpl ?? fetch
  const sleep =
    deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const maxAttempts = deps.maxAttempts ?? REFRESH_MAX_ATTEMPTS

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await doFetch(GRAPHQL_URI, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `mutation RefreshToken($token: String!) { refreshToken(token: $token) { access_token refresh_token } }`,
          variables: { token: refreshToken },
        }),
      })
      let json: any
      try {
        json = await response.json()
      } catch {
        json = null
      }

      const data = json?.data?.refreshToken
      if (data?.access_token && data?.refresh_token) {
        localStorage.setItem("token", data.access_token)
        localStorage.setItem("refreshToken", data.refresh_token)
        return { status: "ok", token: data.access_token }
      }

      const codes: string[] = Array.isArray(json?.errors)
        ? json.errors.map((e: any) => e?.extensions?.code).filter(Boolean)
        : []
      if (codes.some((c) => DEFINITIVE_REFRESH_ERROR_CODES.has(c))) {
        return { status: "definitive" } // no retry, no localStorage write
      }
      // else: HTTP 5xx / errors without a definitive code / missing data → transient, fall through to retry
    } catch {
      // network throw → transient, fall through to retry
    }
    if (attempt < maxAttempts) {
      await sleep(REFRESH_RETRY_DELAYS_MS[attempt - 1] ?? 900)
    }
  }
  return { status: "transient" }
}

/**
 * Handles auth errors that occur while impersonating, ahead of every signOut
 * path. During an impersonation session the admin identity is stashed in
 * previousToken and the session carries no refreshToken. If the impersonation
 * token dies (expired/invalid/malformed), the admin is returned to their own
 * session instead of being signed out.
 *
 * A page fires many queries at once, so multiple errors hit the same expiry.
 * The `isRestoringImpersonation` latch ensures the restore runs exactly once
 * and that every sibling error is swallowed — without it a sibling would see
 * previousToken already cleared and race into signOutUser, kicking the admin to
 * the login page.
 *
 * @returns true if the error was handled here (caller must stop processing it).
 */
export const handleImpersonationAuthError = (err: any): boolean => {
  const isImpersonatingSession =
    !!localStorage.getItem("previousToken") && !localStorage.getItem("refreshToken")
  const isSessionDead =
    err?.name === "TokenExpiredError" ||
    err?.code === "TokenExpiredError" ||
    err?.message === "jwt expired" ||
    err?.message === "jwt malformed" ||
    err?.message === "invalid token" ||
    err?.message === "invalid signature" ||
    err?.message === "Invalid Authorization Token"

  if (isImpersonatingSession && isSessionDead) {
    if (!isRestoringImpersonation) {
      isRestoringImpersonation = true
      restorePreviousSessionAndReload()
    }
    return true
  }
  // Once a restore is navigating, swallow every other error so nothing else can
  // trigger a competing signOut/redirect.
  if (isRestoringImpersonation) {
    return true
  }
  return false
}

// Test-only: reset the module-level restore latch between test cases (in the
// app a restore always triggers a full page reload, which resets it naturally).
export const __resetImpersonationLatchForTests = (): void => {
  isRestoringImpersonation = false
}

/**
 * Re-reads localStorage AFTER the cross-tab lock (or the in-tab mutex) has
 * been acquired: another tab may have already won the race and rotated the
 * tokens while this tab was waiting on the lock. If so, adopt its fresh
 * access token directly — no server call, and critically no re-presentation
 * of the now-stale refresh token (which would otherwise trip reuse detection
 * and revoke the whole family, logging every tab out).
 */
const attemptRefreshWithReRead = async (
  failedAccessToken: string | null,
  preLockRefreshToken: string
): Promise<RefreshResult> => {
  const storedToken = localStorage.getItem("token")
  const storedRefresh = localStorage.getItem("refreshToken")
  if (
    storedToken &&
    (storedToken !== failedAccessToken || storedRefresh !== preLockRefreshToken)
  ) {
    return { status: "ok", token: storedToken } // another tab already rotated; reuse it
  }
  if (!storedRefresh) return { status: "definitive" }
  return performTokenRefresh(storedRefresh)
}

/**
 * Serializes refresh attempts across tabs via the Web Locks API (falls back
 * to the existing per-tab `isRefreshing` mutex when Web Locks is unavailable,
 * e.g. jsdom/jest or older browsers). Acquiring the lock only guarantees
 * exclusivity — the re-read inside `attemptRefreshWithReRead` is what lets a
 * losing tab short-circuit onto the winner's result instead of also hitting
 * the server.
 */
const runRefresh = async (
  failedAccessToken: string | null,
  preLockRefreshToken: string
): Promise<RefreshResult> => {
  if (supportsWebLocks()) {
    return (navigator as any).locks.request(REFRESH_LOCK_NAME, () =>
      attemptRefreshWithReRead(failedAccessToken, preLockRefreshToken)
    )
  }
  return attemptRefreshWithReRead(failedAccessToken, preLockRefreshToken)
}

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }: any) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        // Impersonation guard — MUST run before any signOut branch below.
        if (handleImpersonationAuthError(err)) {
          return
        }

        if (err?.message === "Forbidden Exception") {
          signOutUser(forward, operation)
        }
        if (err?.message === "Unauthorized!") {
          window.location.href = "/un-authorized"
        }
        if (err?.message === "invalid token" || err?.message === "invalid signature") {
          signOutUser(forward, operation)
        }
        if (err?.message === "Invalid Input") {
          console.log("Invalid Input Exception:::", graphQLErrors);
          // toast.error(err?.message)
        }

        // Silent refresh: consolidates both previous TokenExpiredError branches.
        if (
          err?.name === "TokenExpiredError" ||
          err?.message === "jwt expired" ||
          err?.code === "TokenExpiredError"
        ) {
          const storedRefreshToken = localStorage.getItem("refreshToken")
          if (!storedRefreshToken) {
            // A restore is already underway (page is reloading) — a sibling
            // query from the same expiry. Do nothing; must NOT fall through to
            // signOutUser, which would win the navigation and log the admin out.
            if (isRestoringImpersonation) {
              return
            }
            // Impersonation sessions carry no refreshToken by design (see
            // loginAsImpersonation). Ordering here is load-bearing: this check
            // must run BEFORE falling through to signOutUser, otherwise an
            // expired impersonation token would hard-sign-out and wipe
            // previousToken instead of cleanly restoring the admin's session.
            if (localStorage.getItem("previousToken")) {
              isRestoringImpersonation = true
              restorePreviousSessionAndReload()
              return
            }
            // Backward compat: user has no refresh token — sign out once.
            signOutUser(forward, operation)
            return
          }

          return new Observable((observer) => {
            const failedAccessToken = localStorage.getItem("token")
            const preLockRefreshToken = storedRefreshToken

            const applyResult = (result: RefreshResult) => {
              const outcome = refreshResultToOutcome(result)
              if (outcome === "retry") {
                operation.setContext({
                  headers: {
                    authorization: `Bearer ${(result as { status: "ok"; token: string }).token}`,
                  },
                })
                forward(operation).subscribe({
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                })
                return
              }
              if (outcome === "signout") {
                // Definitive server verdict only (REFRESH_TOKEN_INVALID/EXPIRED/
                // REUSE, USER_NOT_FOUND) — a transient failure never reaches here.
                signOutUser(forward, operation)
                observer.complete()
                return
              }
              // 'preserve' (transient): do NOT signOut, do NOT wipe localStorage.
              // Surface the original error so the pending operation errors out;
              // the expired token stays in place so the user's NEXT action
              // retries the refresh once the network is back.
              observer.error(err)
            }

            if (supportsWebLocks()) {
              runRefresh(failedAccessToken, preLockRefreshToken)
                .then(applyResult)
                .catch(() => applyResult({ status: "transient" }))
            } else if (!isRefreshing) {
              isRefreshing = true
              runRefresh(failedAccessToken, preLockRefreshToken)
                .then((result) => {
                  isRefreshing = false
                  notifyPendingResolvers(result)
                  applyResult(result)
                })
                .catch(() => {
                  isRefreshing = false
                  const transientResult: RefreshResult = { status: "transient" }
                  notifyPendingResolvers(transientResult)
                  applyResult(transientResult)
                })
            } else {
              // Another refresh is already in flight — queue and wait.
              new Promise<RefreshResult>((resolve) => {
                pendingResolvers.push(resolve)
              }).then(applyResult)
            }
          })
        }

        if (err?.code === "INTERNAL_SERVER_ERROR") {
          if (
            err?.message === "Owner and sharee list is empty" ||
            err?.message ===
              "You are selecting recruiter level 'Team' but current user is not a part of any team!" ||
            err?.message ===
              "Currently, you are not a part of any Teams Users" ||
            err?.message === "Ownership not found"
          ) {
            console.log("Resticted Toasts")
          }
          // else {
          //   toast.error(err?.message);
          // }
        }
      }
    }
    if (networkError) {
      console.log(`[Network error]: ${networkError}`)
      // if you would also like to retry automatically on
      // network errors, we recommend that you use
      // apollo-link-retry
    }
  }
)

const client = new ApolloClient({
  cache: new InMemoryCache(),
  connectToDevTools: true,
  link: ApolloLink.from([
    errorLink,
    authMiddleware,
    createUploadLink({
      uri: GRAPHQL_URI,
    }),
  ]),
  defaultOptions: defaultOptions,
})

export default client
